import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { workspaceCopy, type WorkspaceCopy } from '../../merchant/copy.js';
import {
  daysSince,
  formatDate,
  formatDuration,
  formatRelative,
  videoStatusLabel,
  type VideoItem,
  type VideoStatusKey,
} from '../../merchant/model.js';
import {
  useMerchantWorkspaceIdentity,
  useWorkspace,
} from '../../merchant/shell-context.js';
import {
  Button,
  ButtonLink,
  Callout,
  Card,
  DefinitionList,
  DownloadIcon,
  EmptyState,
  PageHead,
  Poster,
  SidePanel,
  Tag,
} from '../../merchant/ui/primitives.js';

interface VideoFilters {
  readonly line: string;
  readonly status: 'all' | VideoStatusKey;
  readonly date: 'all' | '0' | '7' | '30';
  readonly language: string;
  readonly platform: string;
  readonly aspect: string;
}

const NO_FILTERS: VideoFilters = {
  line: 'all',
  status: 'all',
  date: 'all',
  language: 'all',
  platform: 'all',
  aspect: 'all',
};

const STATUS_OPTIONS: readonly VideoStatusKey[] = [
  'generating',
  'ready',
  'needs-review',
  'failed',
];

/**
 * The video library from the wireframe, over the server's own read model:
 * every run the merchant owns, filtered by workspace, Production Line,
 * status, date, language, platform and aspect ratio, opened in the side
 * panel. `/videos/line/:id` scopes it to one Production Line.
 */
export function MerchantVideosPage(): ReactNode {
  const { language } = useLanguage();
  const copy = workspaceCopy(language);
  const workspace = useWorkspace();
  const { businessName } = useMerchantWorkspaceIdentity();
  const navigate = useNavigate();
  const { productionLineId: scopedLineId } = useParams<{ productionLineId: string }>();
  const [params, setParams] = useSearchParams();
  const titleId = useId();
  const [filters, setFilters] = useState<VideoFilters>(NO_FILTERS);
  const workspaceName = businessName?.trim() || copy.nav.yourBusiness;
  const scopedLine =
    scopedLineId === undefined
      ? null
      : (workspace.lines.find((line) => line.id === scopedLineId) ?? null);
  const openId = params.get('video');

  const uniq = (pick: (video: VideoItem) => string): readonly string[] => [
    ...new Set(workspace.videos.map(pick).filter((value) => value.length > 0)),
  ];

  const filtered = useMemo(
    () =>
      workspace.videos.filter(
        (video) =>
          (scopedLineId === undefined
            ? filters.line === 'all' || video.lineId === filters.line
            : video.lineId === scopedLineId) &&
          (filters.status === 'all' || video.status === filters.status) &&
          (filters.date === 'all' || daysSince(video.date) <= Number(filters.date)) &&
          (filters.language === 'all' || video.language === filters.language) &&
          (filters.platform === 'all' || video.platform === filters.platform) &&
          (filters.aspect === 'all' || video.aspect === filters.aspect),
      ),
    [filters, scopedLineId, workspace.videos],
  );
  const active = Object.values(filters).some((value) => value !== 'all');
  const open =
    openId === null
      ? null
      : (workspace.videos.find((video) => video.id === openId) ?? null);

  useEffect(() => {
    if (openId !== null && !workspace.loading && open === null) {
      params.delete('video');
      setParams(params, { replace: true });
    }
  }, [open, openId, params, setParams, workspace.loading]);

  const setFilter = <K extends keyof VideoFilters>(key: K, value: VideoFilters[K]) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const selects: readonly {
    readonly key: keyof VideoFilters;
    readonly label: string;
    readonly all: string;
    readonly options: readonly { readonly value: string; readonly label: string }[];
  }[] = [
    ...(scopedLine === null
      ? [
          {
            key: 'line' as const,
            label: copy.videos.line,
            all: copy.videos.allLines,
            options: workspace.lines.map((line) => ({
              value: line.id,
              label: line.title,
            })),
          },
        ]
      : []),
    {
      key: 'status',
      label: copy.videos.status,
      all: copy.videos.anyStatus,
      options: STATUS_OPTIONS.map((status) => ({
        value: status,
        label: videoStatusLabel(status, copy),
      })),
    },
    {
      key: 'date',
      label: copy.videos.date,
      all: copy.videos.anyDate,
      options: [
        { value: '0', label: copy.videos.today },
        { value: '7', label: copy.videos.last7 },
        { value: '30', label: copy.videos.last30 },
      ],
    },
    {
      key: 'language',
      label: copy.videos.language,
      all: copy.videos.anyLanguage,
      options: uniq((video) => video.language).map((value) => ({
        value,
        label: value,
      })),
    },
    {
      key: 'platform',
      label: copy.videos.platform,
      all: copy.videos.anyPlatform,
      options: uniq((video) => video.platform).map((value) => ({
        value,
        label: value,
      })),
    },
    {
      key: 'aspect',
      label: copy.videos.aspect,
      all: copy.videos.anyAspect,
      options: uniq((video) => video.aspect).map((value) => ({ value, label: value })),
    },
  ];

  return (
    <section
      className="mj-ws-page"
      aria-labelledby={titleId}
      data-screen-label="Videos"
    >
      <PageHead
        eyebrow={
          scopedLine === null
            ? copy.videos.allWorkspaces
            : `${workspaceName} · ${scopedLine.title}`
        }
        title={copy.videos.title}
        titleId={titleId}
        intro={scopedLine === null ? copy.videos.desc : copy.videos.scopedDesc}
        action={
          scopedLine === null ? undefined : (
            <ButtonLink to="/videos">{copy.videos.showAll}</ButtonLink>
          )
        }
      />

      <div
        className="mj-ws-toolbar is-tight"
        role="group"
        aria-label={copy.videos.filters}
      >
        {scopedLine === null ? (
          <label>
            <span className="mj-ws-vh">{copy.videos.workspace}</span>
            <select
              className="mj-ws-inp is-compact"
              value="current"
              onChange={() => undefined}
            >
              <option value="current">{workspaceName}</option>
            </select>
          </label>
        ) : null}
        {selects.map((select) => (
          <label key={select.key}>
            <span className="mj-ws-vh">{select.label}</span>
            <select
              className="mj-ws-inp is-compact"
              value={filters[select.key]}
              onChange={(event) => {
                setFilter(select.key, event.target.value);
              }}
            >
              <option value="all">{select.all}</option>
              {select.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ))}
        {active ? (
          <Button
            variant="quiet"
            small
            onClick={() => {
              setFilters(NO_FILTERS);
            }}
          >
            {copy.videos.clear}
          </Button>
        ) : null}
        <span
          className="mj-ws-muted mj-ws-sm mj-ws-tnum mj-ws-toolbar-count"
          aria-live="polite"
        >
          {copy.common.videosCount(filtered.length)}
        </span>
      </div>

      {workspace.loading ? (
        <p className="mj-ws-loading" role="status">
          {copy.common.loading}
        </p>
      ) : filtered.length > 0 ? (
        <div className="mj-ws-vgrid">
          {filtered.map((video) => (
            <Card key={video.id} as="article" className="mj-ws-vcard" padding={0}>
              <Poster
                state={
                  video.status === 'generating' || video.status === 'card'
                    ? 'generating'
                    : video.status === 'failed'
                      ? 'failed'
                      : 'playable'
                }
                posterUrl={video.posterUrl}
                duration={formatDuration(video.durationSeconds)}
                label={`${video.aspect} · ${video.platform || copy.common.dash}`}
                ariaLabel={copy.videos.open(video.title)}
                onClick={() => {
                  params.set('video', video.id);
                  setParams(params);
                }}
              />
              <div style={{ minWidth: 0 }}>
                <h3 className="mj-ws-h3">{video.title}</h3>
                <div
                  className="mj-ws-muted mj-ws-xs mj-ws-mt-8"
                  style={{ marginTop: 2 }}
                >
                  {workspaceName} · {video.lineTitle}
                </div>
                <div className="mj-ws-vcard-meta">
                  <Tag status={video.status}>
                    {videoStatusLabel(video.status, copy)}
                  </Tag>
                  <span className="mj-ws-muted mj-ws-xs mj-ws-tnum">
                    {formatDate(video.date, language)}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          title={active ? copy.videos.noMatch : copy.videos.noVideos}
          body={active ? copy.videos.noMatchBody : copy.videos.noVideosBody}
          action={
            active ? (
              <Button
                small
                onClick={() => {
                  setFilters(NO_FILTERS);
                }}
              >
                {copy.videos.clear}
              </Button>
            ) : undefined
          }
        >
          <Poster width={60} state="placeholder" />
        </EmptyState>
      )}

      {open !== null ? (
        <VideoDetailPanel
          video={open}
          copy={copy}
          language={language}
          workspaceName={workspaceName}
          onClose={() => {
            params.delete('video');
            setParams(params);
          }}
          onOpenLine={() => {
            if (open.lineId !== null)
              void navigate(`/production-lines/${encodeURIComponent(open.lineId)}`);
          }}
        />
      ) : null}
    </section>
  );
}

export function VideoDetailPanel({
  video,
  copy,
  language,
  workspaceName,
  onClose,
  onOpenLine,
}: {
  readonly video: VideoItem;
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly workspaceName: string;
  readonly onClose: () => void;
  readonly onOpenLine: () => void;
}): ReactNode {
  const history = [
    video.run.video !== null && video.run.confirmedAt !== null
      ? {
          when: video.run.confirmedAt,
          text: copy.line.activity.videoReady(video.title),
        }
      : null,
    video.run.confirmedAt !== null
      ? { when: video.run.confirmedAt, text: copy.line.activity.cardConfirmed }
      : null,
    {
      when: video.run.authorizedAt,
      text: copy.line.activity.videoStarted(video.direction || video.title),
    },
  ].filter((item): item is { when: string; text: string } => item !== null);

  return (
    <SidePanel
      eyebrow={copy.videos.video}
      title={video.title}
      onClose={onClose}
      closeLabel={copy.videos.closeDetails}
      footer={
        <>
          {video.downloadable && video.videoUrl !== null ? (
            <a className="mj-ws-btn mj-ws-btn-p" href={video.videoUrl} download>
              <DownloadIcon />
              {copy.line.download}
            </a>
          ) : (
            <Button variant="primary" disabled>
              <DownloadIcon />
              {copy.line.download}
            </Button>
          )}
          <Button onClick={onOpenLine} disabled={video.lineId === null}>
            {copy.videos.openLine}
          </Button>
        </>
      }
    >
      <div className="mj-ws-panel-lead">
        {video.videoUrl !== null ? (
          <video
            className="mj-ws-video"
            style={{ width: 120, flex: 'none' }}
            controls
            preload="metadata"
            poster={video.posterUrl ?? undefined}
            src={video.videoUrl}
          />
        ) : (
          <Poster
            width={120}
            state={
              video.status === 'generating' || video.status === 'card'
                ? 'generating'
                : video.status === 'failed'
                  ? 'failed'
                  : 'placeholder'
            }
            duration={formatDuration(video.durationSeconds)}
          />
        )}
        <DefinitionList
          columns="narrow"
          className="mj-ws-sm"
          rows={[
            [
              copy.videos.status,
              <Tag key="s" status={video.status}>
                {videoStatusLabel(video.status, copy)}
              </Tag>,
            ],
            [copy.line.workspace, workspaceName],
            [copy.line.productionLine, video.lineTitle || copy.common.dash],
            [
              copy.line.duration,
              formatDuration(video.durationSeconds) ?? copy.common.dash,
            ],
            [copy.line.language, video.language || copy.common.dash],
            [
              copy.line.format,
              `${video.aspect} · ${video.platform || copy.common.dash}`,
            ],
            [copy.videos.date, formatDate(video.date, language)],
          ]}
        />
      </div>
      {video.status === 'failed' ? (
        <Callout className="mj-ws-sm">
          <strong>{copy.videos.couldNotCreate}</strong> {video.failReason ?? ''}
        </Callout>
      ) : null}
      {video.status === 'needs-review' ? (
        <Callout className="mj-ws-sm">
          <strong>{copy.videos.needsReview}</strong> {copy.videos.needsReviewBody}
        </Callout>
      ) : null}
      {video.run.card !== null ? (
        <>
          <section className="mj-ws-panel-section">
            <div className="mj-ws-eyebrow">{copy.line.cardOpens}</div>
            <p className="mj-ws-sm">“{video.run.card.hookLine}”</p>
          </section>
          <section className="mj-ws-panel-section">
            <div className="mj-ws-eyebrow">{copy.line.direction}</div>
            <p className="mj-ws-sm">{video.run.card.direction}</p>
          </section>
        </>
      ) : null}
      <section className="mj-ws-panel-section">
        <div className="mj-ws-eyebrow">{copy.videos.generationHistory}</div>
        <ol className="mj-ws-act">
          {history.map((item, index) => (
            <li key={String(index)}>
              <span className="mj-ws-muted mj-ws-xs mj-ws-tnum">
                {formatRelative(item.when, copy, language)}
              </span>
              <span className="mj-ws-sm">{item.text}</span>
            </li>
          ))}
        </ol>
      </section>
    </SidePanel>
  );
}
